import React, { useEffect, useMemo } from 'react';
import { Input, Multiselect, Textarea } from '@cokitchen/cokitchen-components';
import BrandStatus from 'components/brand-status';
import useNewBrandContext from 'contexts/new-brand.context';
import { ENUM_BRAND_TYPE } from 'utils/enum';
import { generateSlug, isFCBBrand, isFCBCustomBrand } from 'utils/functions';
import {
  FCB_BRAND_TYPE_OPTIONS,
  PROVIDER_BRAND_TYPE_OPTIONS,
  FC_BRAND_TYPE_OPTIONS
} from 'utils/constants';
import TimeSelector from 'components/base/time-selector';

const CreateBrandDetails = () => {
  const {
    createBrandForm,
    companyOptions,
    loadingCompanyOptions,
    setCreateBrandForm,
    setDisabled
  } = useNewBrandContext();
  useEffect(() => {
    if (!isFCBBrand(createBrandForm.brand_type))
      setCreateBrandForm({ ...createBrandForm, cutoff_time: null });
  }, [createBrandForm.brand_type]);

  useEffect(() => {
    const disabled =
      ![
        createBrandForm.name,
        createBrandForm.short_text,
        createBrandForm.summary,
        createBrandForm.description
      ].every(Boolean) ||
      (isFCBBrand(createBrandForm.brand_type) &&
        !createBrandForm.all_company_available &&
        !createBrandForm.company_ids.length);
    setDisabled(disabled);
  }, [createBrandForm]);

  const isFoodCourt = useMemo(() => {
    const isInOptions = FC_BRAND_TYPE_OPTIONS.some(
      (opt) => opt.value === createBrandForm.brand_type
    );

    const isOthers =
      createBrandForm.preorder ||
      createBrandForm.is_private ||
      isFCBBrand(createBrandForm.brand_type);

    return isInOptions && !isOthers;
  }, [createBrandForm.brand_type, createBrandForm.preorder, createBrandForm.is_private]);
  const brandSlug = generateSlug(createBrandForm.name);

  return (
    <div>
      <Input
        type='text'
        label='Brand Name'
        placeholder='Enter brand name'
        className='w-full mt-4'
        inputClass='px-[20px] py-[14px]'
        value={createBrandForm?.name}
        onChange={(e) => {
          const name = e.target.value;
          setCreateBrandForm({
            ...createBrandForm,
            name,
            slug: brandSlug
          });
        }}
      />
      <Input
        type='text'
        label='Brand Slug'
        placeholder='Enter brand slug'
        className='w-full mt-4'
        inputClass='px-[20px] py-[14px]'
        value={createBrandForm?.slug || ''}
        onChange={(e) =>
          setCreateBrandForm({
            ...createBrandForm,
            slug: e.target.value
          })
        }
      />
      {createBrandForm?.slug && (
        <p className='text-gray-5 mt-1'>
          Preview: https://getfoodcourt.com/restaurants/{createBrandForm?.slug}
        </p>
      )}
      <Input
        type='text'
        label='Brand Tagline'
        placeholder='Enter brand tagline'
        className='w-full mt-4'
        inputClass='px-[20px] py-[14px]'
        value={createBrandForm?.short_text}
        maxLength={15}
        onChange={(e) => setCreateBrandForm({ ...createBrandForm, short_text: e.target.value })}
      />
      <Input
        type='text'
        label='Brand Summary'
        placeholder='Enter brand summary'
        className='w-full my-4'
        inputClass='px-[20px] py-[14px]'
        value={createBrandForm?.summary}
        onChange={(e) => setCreateBrandForm({ ...createBrandForm, summary: e.target.value })}
      />
      <Textarea
        className='w-full'
        placeholder='Describe the brand'
        value={createBrandForm?.description}
        onChange={(e) => setCreateBrandForm({ ...createBrandForm, description: e.target.value })}
        label='Brand Description'
      />
      <div className='flex gap-4'>
        <Input
          type='radio'
          name='brandType'
          checked={createBrandForm.preorder}
          onChange={(e) => {
            setCreateBrandForm({
              ...createBrandForm,
              is_private: false,
              brand_type: ENUM_BRAND_TYPE.FOODCOURT,
              preorder: e.target.checked
            });
          }}
          label='Pre order'
          className='mt-3 ml-2'
        />
        <Input
          type='radio'
          name='brandType'
          checked={createBrandForm.is_private}
          onChange={(e) => {
            setCreateBrandForm({
              ...createBrandForm,
              preorder: false,
              brand_type: ENUM_BRAND_TYPE.FOODCOURT,
              is_private: e.target.checked
            });
          }}
          label='Private'
          className='mt-3 ml-2'
        />
        <Input
          type='radio'
          name='brandType'
          checked={isFCBBrand(createBrandForm.brand_type)}
          onChange={(e) => {
            setCreateBrandForm({
              ...createBrandForm,
              preorder: false,
              is_private: false,
              brand_type: e.target.checked
                ? ENUM_BRAND_TYPE.FOODCOURT_BUSINESS
                : ENUM_BRAND_TYPE.FOODCOURT
            });
          }}
          label='FCB'
          className='mt-3 ml-2'
        />
        <Input
          type='radio'
          name='brandType'
          checked={isFoodCourt}
          onChange={() => {
            setCreateBrandForm({
              ...createBrandForm,
              preorder: false,
              is_private: false,
              brand_type: ENUM_BRAND_TYPE.FOODCOURT
            });
          }}
          label='Foodcourt'
          className='mt-3 ml-2'
        />
      </div>

      {isFoodCourt && (
        <div className='flex flex-col gap-1 my-6'>
          <p className='font-medium'>Brand Type</p>
          <Multiselect
            isSingle
            placeholder='Select brand type'
            values={[createBrandForm.brand_type || ENUM_BRAND_TYPE.FOODCOURT]}
            onChange={(values) =>
              setCreateBrandForm({
                ...createBrandForm,
                brand_type: values[0] as ENUM_BRAND_TYPE
              })
            }
            options={FC_BRAND_TYPE_OPTIONS}
          />
        </div>
      )}
      {createBrandForm?.preorder && (
        <Input
          type='number'
          small
          min={0}
          max={100}
          label='Pre order duration in hours'
          value={createBrandForm?.preorder_duration_in_hours}
          onChange={(e) =>
            setCreateBrandForm({
              ...createBrandForm,
              preorder_duration_in_hours: parseInt(e.target.value)
            })
          }
          className='mt-5'
        />
      )}
      {createBrandForm?.is_private && (
        <div className='flex flex-col gap-1 my-6'>
          <p className='font-medium'>Brand Type</p>
          <Multiselect
            isSingle
            placeholder='Select brand type'
            values={[createBrandForm.brand_type]}
            onChange={(values) =>
              setCreateBrandForm({ ...createBrandForm, brand_type: values[0] as ENUM_BRAND_TYPE })
            }
            options={PROVIDER_BRAND_TYPE_OPTIONS}
          />

          {createBrandForm.brand_type === ENUM_BRAND_TYPE.GLOVO && (
            <Input
              className='my-4'
              type='text'
              small
              label='Store ID'
              value={createBrandForm.brand_partnership_id}
              onChange={(e: any) =>
                setCreateBrandForm({
                  ...createBrandForm,
                  brand_partnership_id: e.target.value
                })
              }
            />
          )}
        </div>
      )}

      {isFCBBrand(createBrandForm.brand_type) ? (
        <div className='flex flex-col gap-6 my-6'>
          <div className='flex flex-col gap-1'>
            <p className='font-medium'>Brand Type</p>
            <Multiselect
              isSingle
              placeholder='Select brand type'
              values={
                createBrandForm?.preorder || isFCBCustomBrand(createBrandForm.brand_type)
                  ? [createBrandForm.brand_type]
                  : []
              }
              onChange={(values) =>
                setCreateBrandForm({ ...createBrandForm, brand_type: values[0] as ENUM_BRAND_TYPE })
              }
              options={
                createBrandForm?.preorder
                  ? [{ value: ENUM_BRAND_TYPE.FOODCOURT_BUSINESS, text: 'FoodCourt Business' }]
                  : FCB_BRAND_TYPE_OPTIONS
              }
            />
          </div>

          {!createBrandForm?.preorder && (
            <div className='flex flex-col gap-3'>
              <p className='font-medium'>Cut-off Time</p>
              <TimeSelector
                value={createBrandForm.cutoff_time || {}}
                onChange={(cutoff_time) => setCreateBrandForm({ ...createBrandForm, cutoff_time })}
              />
            </div>
          )}

          <div className='flex flex-col gap-3 my-3'>
            <p className='font-medium'>Companies</p>
            <div className='flex items-center gap-6'>
              <Input
                type='checkbox'
                label='Available to all companies'
                checked={createBrandForm.all_company_available}
                onChange={() =>
                  setCreateBrandForm({
                    ...createBrandForm,
                    all_company_available: true
                  })
                }
              />
              <Input
                type='checkbox'
                label='Available to selected companies'
                checked={!createBrandForm.all_company_available}
                onChange={() =>
                  setCreateBrandForm({
                    ...createBrandForm,
                    all_company_available: false
                  })
                }
              />
            </div>

            {!createBrandForm.all_company_available && (
              <Multiselect
                hasSearch
                filterResults
                placeholder='Select companies'
                loading={loadingCompanyOptions}
                values={createBrandForm.company_ids}
                onChange={(company_ids) => setCreateBrandForm({ ...createBrandForm, company_ids })}
                options={companyOptions}
              />
            )}
          </div>
        </div>
      ) : null}
      <div className='my-8'>
        <BrandStatus
          isNew={createBrandForm?.new}
          isTop={createBrandForm?.top}
          popup={createBrandForm?.pop_up}
          comingSoon={createBrandForm?.coming_soon}
          setComingSoon={(k: boolean) => setCreateBrandForm({ ...createBrandForm, coming_soon: k })}
          setNew={(k: boolean) => setCreateBrandForm({ ...createBrandForm, new: k })}
          setPopup={(k: boolean) => setCreateBrandForm({ ...createBrandForm, pop_up: k })}
          comment={createBrandForm?.coming_soon_text}
          setComment={(k: string) =>
            setCreateBrandForm({ ...createBrandForm, coming_soon_text: k })
          }
          color={createBrandForm?.popup_color}
          setColor={(k: string) => setCreateBrandForm({ ...createBrandForm, popup_color: k })}
          setIsTop={(k: boolean) => setCreateBrandForm({ ...createBrandForm, top: k })}
          className='my-6'
        />
      </div>
    </div>
  );
};

export default CreateBrandDetails;
